import { ReceivingAreaProfileMapping } from '@ekkogmbh/apisdk';
import { Stop } from '@mui/icons-material';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import TooltipHardened from 'src/Common/Components/TooltipHardened';

export default function ReceivingAreaMappingChip({
  mapping,
}: {
  mapping: ReceivingAreaProfileMapping;
}): React.JSX.Element {
  const tooltipContent: ReactNode = useMemo(
    () => (
      <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 180 }}>
        <Stack spacing={0.5}>
          <Typography
            variant="caption"
            fontWeight={700}
            textTransform="uppercase"
            color="text.secondary"
            letterSpacing={0.5}
          >
            Observed States
          </Typography>
          {mapping.observedStates.map((state: string) => (
            <Stack key={state} direction="row" alignItems="center" spacing={0.5}>
              <Stop sx={{ fontSize: 16, color: mapping.stateColors[state] }} />
              <Typography variant="body2">{state}</Typography>
            </Stack>
          ))}
        </Stack>
        {mapping.observedFields.length > 0 && (
          <>
            <Divider />
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                fontWeight={700}
                textTransform="uppercase"
                color="text.secondary"
                letterSpacing={0.5}
              >
                Observed Fields
              </Typography>
              {mapping.observedFields.map((fieldKey: string) => (
                <Typography key={fieldKey} variant="body2" fontFamily="monospace">
                  {fieldKey}
                </Typography>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    ),
    [mapping.observedFields, mapping.observedStates, mapping.stateColors],
  );

  const chipContent: ReactNode = useMemo(
    () => (
      <Stack>
        <Typography
          color={'textPrimary'}
          style={{ fontSize: 16, lineHeight: 'normal', fontWeight: 700, userSelect: 'none' }}
        >
          {mapping.receivingArea.name}
        </Typography>
        <Typography color={'textSecondary'} style={{ fontSize: 12, lineHeight: 'normal', userSelect: 'none' }}>
          {mapping.receivingArea.coordinate}
        </Typography>
      </Stack>
    ),
    [mapping.receivingArea],
  );

  return (
    <TooltipHardened arrow tooltip={tooltipContent}>
      <Chip
        variant={'outlined'}
        style={{
          position: 'relative',
          margin: 8,
          height: 48,
          width: 'fit-content',
          overflow: 'hidden',
          flexShrink: 0,
        }}
        label={chipContent}
      />
    </TooltipHardened>
  );
}
